import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseAudioPlaybackReturn {
  playingId: string | null;
  playAudio: (id: string, url: string) => void;
  stopAudio: () => void;
  isPlaying: (id: string) => boolean;
}

export const useAudioPlayback = (): UseAudioPlaybackReturn => {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = useCallback(
    (id: string, url: string) => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (playingId === id) {
        setPlayingId(null);
        return;
      }

      const audio = new Audio(url);
      audioRef.current = audio;
      setPlayingId(id);

      audio.addEventListener('ended', () => {
        setPlayingId(null);
      });

      audio.addEventListener('error', () => {
        console.error('Error playing audio preview');
        setPlayingId(null);
      });

      audio.play().catch((error) => {
        console.error('Error playing audio:', error);
        setPlayingId(null);
      });
    },
    [playingId]
  );

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
  }, []);

  const isPlaying = useCallback(
    (id: string) => {
      return playingId === id;
    },
    [playingId]
  );

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingId(null);
    };
  }, []);

  return {
    playingId,
    playAudio,
    stopAudio,
    isPlaying,
  };
};
